import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function VPSPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  const vpsPlans = [
    {
      name: 'VPS Básico',
      monthlyPrice: 79990,
      annualPrice: 63990,
      popular: false,
      specs: {
        cpu: '2 vCPU',
        ram: '4 GB RAM',
        storage: '80 GB SSD',
        bandwidth: 'Tráfico Ilimitado'
      },
      features: [
        'Acceso Root Completo',
        'Panel de Control cPanel',
        'Respaldos Diarios',
        'Monitoreo 24/7',
        'Soporte Técnico',
        'Firewall Incluido'
      ]
    },
    {
      name: 'VPS Profesional',
      monthlyPrice: 110990,
      annualPrice: 88990,
      popular: true,
      specs: {
        cpu: '4 vCPU',
        ram: '8 GB RAM',
        storage: '150 GB SSD',
        bandwidth: 'Tráfico Ilimitado',
        ip: '1 IP Dedicada'
      },
      features: [
        'Acceso Root Completo',
        'Panel de Control cPanel',
        'Respaldos Diarios',
        'Monitoreo 24/7',
        'Soporte Prioritario',
        'Firewall Avanzado',
        'Migración Gratis',
        'DDoS Protection'
      ]
    },
    {
      name: 'VPS Empresarial',
      monthlyPrice: 149990,
      annualPrice: 119990,
      popular: false,
      specs: {
        cpu: '8 vCPU',
        ram: '16 GB RAM',
        storage: '150 GB SSD',
        bandwidth: 'Tráfico Ilimitado',
        ip: '2 IP Dedicadas'
      },
      features: [
        'Acceso Root Completo',
        'Panel de Control cPanel',
        'Respaldos Diarios',
        'Monitoreo Avanzado 24/7',
        'Soporte VIP',
        'Firewall Enterprise',
        'Migración Gratis',
        'DDoS Protection Pro',
        'CDN Incluido',
        'Balanceo de Carga'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-cpu-line',
      title: 'CPU Dedicado',
      description: 'Recursos de procesamiento dedicados exclusivamente para tu servidor sin compartir con otros usuarios'
    },
    {
      icon: 'ri-server-line',
      title: 'IP Dedicada',
      description: 'Dirección IP exclusiva para tu VPS, ideal para aplicaciones empresariales y certificados SSL'
    },
    {
      icon: 'ri-save-line',
      title: 'Respaldos Diarios',
      description: 'Copias de seguridad automáticas diarias para proteger tu información y restaurar en cualquier momento'
    },
    {
      icon: 'ri-terminal-box-line',
      title: 'Acceso Root',
      description: 'Control total sobre tu servidor con acceso root completo y SSH'
    },
    {
      icon: 'ri-arrow-up-down-line',
      title: 'Escalabilidad',
      description: 'Aumenta recursos (CPU, RAM, almacenamiento) en minutos sin tiempo de inactividad'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Seguridad Avanzada',
      description: 'Firewall configurable, protección DDoS y monitoreo de seguridad 24/7'
    },
    {
      icon: 'ri-speed-line',
      title: 'Tráfico Ilimitado',
      description: 'Sin límites de transferencia mensual, ideal para sitios con alto volumen de visitas'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Panel de Control cPanel',
      description: 'Panel de control intuitivo para gestionar tu VPS fácilmente con todas las herramientas necesarias'
    }
  ];

  const useCases = [
    {
      icon: 'ri-shopping-cart-line',
      title: 'E-commerce',
      description: 'Tiendas online con alto tráfico que requieren recursos dedicados y máxima disponibilidad'
    },
    {
      icon: 'ri-code-box-line',
      title: 'Aplicaciones Web',
      description: 'Desarrollo y hosting de aplicaciones personalizadas con frameworks modernos'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Bases de Datos',
      description: 'Servidores de bases de datos MySQL, PostgreSQL, MongoDB con alto rendimiento'
    },
    {
      icon: 'ri-game-line',
      title: 'Servidores de Juegos',
      description: 'Hosting para servidores de juegos multijugador con baja latencia'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Corporativo',
      description: 'Servidores de correo empresarial con control total y privacidad'
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Desarrollo y Testing',
      description: 'Entornos de desarrollo, staging y testing aislados y configurables'
    }
  ];

  const operatingSystems = [
    { name: 'Ubuntu', icon: 'ri-ubuntu-line' },
    { name: 'CentOS', icon: 'ri-centos-line' },
    { name: 'Debian', icon: 'ri-debian-line' },
    { name: 'AlmaLinux', icon: 'ri-server-line' },
    { name: 'Rocky Linux', icon: 'ri-server-line' },
    { name: 'Windows Server', icon: 'ri-windows-line' }
  ];

  return (
    <>
      <Helmet>
        <title>Servidores VPS en Chile | Recursos Dedicados y Acceso Root Completo</title>
        <meta name="description" content="Servidores VPS en Chile con recursos dedicados, acceso root completo y SSD NVMe ultra rápido. Planes escalables desde $79.990 mensuales con IP dedicada y snapshots automáticos en Santiago." />
        <meta name="keywords" content="vps chile, servidor virtual chile, vps santiago, servidor dedicado virtual, vps con root, servidor escalable chile, vps empresarial, hosting vps chile" />
        <link rel="canonical" href="https://www.ehostingchile.com/vps" />
        <meta property="og:title" content="Servidores VPS en Chile | Recursos Dedicados y Acceso Root Completo" />
        <meta property="og:description" content="Servidores VPS en Chile con recursos dedicados, acceso root completo y SSD NVMe ultra rápido. Planes escalables desde $79.990 mensuales." />
        <meta property="og:url" content="https://www.ehostingchile.com/vps" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Servidores VPS",
            "description": "Servidores virtuales privados en Chile con recursos dedicados y acceso root completo",
            "brand": {
              "@type": "Brand",
              "name": "EhostingChile"
            },
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "79990",
              "highPrice": "149990",
              "priceCurrency": "CLP",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 text-white py-20 sm:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-300 rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6">Servidores VPS en Chile</h1>
              <p className="text-base sm:text-xl text-purple-100 mb-6 sm:mb-8">
                Servidores virtuales privados con recursos dedicados, control total y máximo rendimiento.
                Ideal para aplicaciones empresariales, e-commerce y proyectos de alto tráfico.
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-4 justify-center">
                {['SSD NVMe', 'Acceso Root', 'IP Dedicada', '99.9% Uptime'].map((item) => (
                  <div key={item} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full">
                    <i className="ri-check-line text-green-300 text-sm"></i>
                    <span className="text-xs sm:text-sm whitespace-nowrap">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Billing Toggle */}
        <section className="py-8 sm:py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex justify-center items-center gap-3 sm:gap-4">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>Mensual</span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="relative w-14 h-7 bg-indigo-600 rounded-full transition-colors cursor-pointer"
              >
                <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${billingCycle === 'annual' ? 'translate-x-7' : ''}`}></span>
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>Anual</span>
              <span className="ml-1 sm:ml-2 px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">Ahorra hasta 20%</span>
            </div>
          </div>
        </section>

        {/* VPS Plans */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Planes VPS Optimizados</h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Elige el plan VPS que mejor se adapte a tus necesidades de rendimiento y escalabilidad
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {vpsPlans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 ${
                    plan.popular ? 'ring-2 ring-indigo-600 sm:transform sm:scale-105' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 sm:px-6 py-1 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap">
                      Más Popular
                    </div>
                  )}
                  <div className="p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-5 sm:mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                          ${billingCycle === 'monthly' ? plan.monthlyPrice.toLocaleString('es-CL') : plan.annualPrice.toLocaleString('es-CL')}
                        </span>
                        <span className="text-gray-600 text-sm">/mes</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-xs sm:text-sm text-green-600 mt-1">Facturado anualmente</p>
                      )}
                    </div>

                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <i className="ri-cpu-line text-indigo-600 text-sm sm:text-base"></i>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900">{plan.specs.cpu}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-database-2-line text-indigo-600 text-sm sm:text-base"></i>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900">{plan.specs.ram}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-hard-drive-2-line text-indigo-600 text-sm sm:text-base"></i>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900">{plan.specs.storage}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-speed-line text-indigo-600 text-sm sm:text-base"></i>
                          <span className="text-xs sm:text-sm font-semibold text-gray-900">{plan.specs.bandwidth}</span>
                        </div>
                        {plan.specs.ip && (
                          <div className="flex items-center gap-2">
                            <i className="ri-global-line text-indigo-600 text-sm sm:text-base"></i>
                            <span className="text-xs sm:text-sm font-semibold text-gray-900">{plan.specs.ip}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm">
                          <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contratar"
                      className={`block w-full text-center py-2.5 sm:py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base ${
                        plan.popular
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Configurar VPS
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Beneficios de Nuestros VPS</h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">Características premium incluidas en todos nuestros planes VPS</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 sm:p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-indigo-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                    <i className={`${benefit.icon} text-xl sm:text-2xl text-indigo-600`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Casos de Uso Ideales</h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">Nuestros servidores VPS son perfectos para múltiples aplicaciones y proyectos</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-white rounded-xl p-5 sm:p-6 shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                    <i className={`${useCase.icon} text-lg sm:text-xl text-purple-600`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Operating Systems */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Sistemas Operativos Disponibles</h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">Elige el sistema operativo que prefieras para tu servidor VPS</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
              {operatingSystems.map((os, index) => (
                <div key={index} className="bg-gray-50 rounded-xl px-5 sm:px-8 py-4 sm:py-6 shadow-md hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <i className={`${os.icon} text-2xl sm:text-3xl text-gray-700`}></i>
                    <span className="text-base sm:text-lg font-semibold text-gray-900 whitespace-nowrap">{os.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">¿Necesitas un Servidor VPS Personalizado?</h2>
            <p className="text-base sm:text-xl text-purple-100 mb-6 sm:mb-8">
              Contáctanos para configurar un VPS a medida con los recursos exactos que necesitas
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center">
              <Link to="/contratar" className="bg-white text-indigo-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base">
                Configurar VPS
              </Link>
              <Link to="/soporte" className="bg-indigo-500 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-indigo-400 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base">
                Hablar con un Experto
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}